import React, { useMemo } from 'react'
import log from 'loglevel'
import { Flags, FlagsProvider } from 'react-feature-flags'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import {
  FeatureAccessRequest,
  FeatureAccessResponse,
  FEATURE_ACCESS,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useUser } from 'src/components/async/Users'
import { usePlaybooks } from 'src/pages/TrusteroIntelligence/playbooks/playbooks.hooks'
import { useTrustPortalAccountId } from 'src/pages/TrustContentManagement/trustPortalConfiguration.hooks'
import { isNewerVersion } from 'src/Utils/semver'
import { loadFromStorage } from '../Utils/storage'
import { EnvConfig } from '../config/config'
import {
  isDeniedInternalEmail,
  useIsAdmin,
  useIsInternalUser,
  useIsProvider,
  useIsTrustPortalApprover,
  useIsTrustPortalPublisher,
} from './authContext'

const logger = log.getLogger('context')

export enum GatekeeperFlag {
  REGISTER_PAGES = 'registerPages',
  RELEVANT_DATE = 'relevantDate',
  MULTI_FRAMEWORK = 'multiFramework',
  CUSTOM_RECEPTOR = 'customReceptor',
  GRID_COLUMN_SORT = 'gridColumnSort',
  SUGGESTED_AUTOMATED_EVIDENCE = 'suggestedAutomatedEvidence',
  MULTI_FRAMEWORK_SUGGESTIONS = 'multiFrameworkSuggestions',
  STUB_FRAMEWORKS = 'stubFrameworks',
  REPORT_SCAN = 'reportScan',
  SUGGESTED_DLR_EVIDENCE = 'suggestedDLREvidence',
  CRITERIA_CONTROL_MAPPING = 'criteriaControlMapping',
  CONTROL_IDS = 'controlIds',
  RISK_REGISTER = 'riskRegister',
  STANDALONE_SCANNER = 'standaloneScanner',
  SNYK_RECEPTOR = 'snykReceptor',
  AUDIT_BOT = 'auditBot',
  SERVICES_OVERHAUL = 'servicesOverhaul',
  MIRADORE_RECEPTOR = 'miradoreReceptor',
  SOPHOS_RECEPTOR = 'sophosReceptor',
  VENDOR_MANAGEMENT = 'vendorManagement',
  JUMPCLOUD_RECEPTOR = 'jumpcloudReceptor',
  MS_LOGIN = 'msLogin',
  KNOWBE4_RECEPTOR = 'knowbe4Receptor',
  MULTI_FRAMEWORK_AUDITBOT = 'multiFrameworkAuditbot',
  SERVICE_ACCOUNTS = 'serviceAccounts',
  ASK_AI_COPILOT = 'askAiCopilot',
  CC_ROADMAP = 'ccRoadmap',
  CONTINUOUS_MONITORING = 'continuousMonitoring',
  GOOGLE_DRIVE_AUTH = 'googleDriveAuth',
  JIRA_RECEPTOR = 'jiraReceptor',
  GOOGLE_DRIVE_POLICY_IMPORT = 'googleDrivePolicyImport',
  POLICY_RESPONSIBILITY = 'policyResponsibility',
  NEW_EVIDENCE_TABLE = 'newEvidenceTable',
  CONTROL_EVIDENCE_NEW_TAB = 'controlEvidenceNewTab',
  GENERATE_SYSTEM_DESCRIPTION = 'generateSystemDescription',
  RELEVANT_DATE_FORM = 'relevantDateForm',
  EVIDENCE_INDEX = 'evidenceIndex',
  SLACK_RECEPTOR = 'slackReceptor',
  SONARQUBE_CLOUD_RECEPTOR = 'sonarqubeCloudReceptor',
  FRESHDESK_RECEPTOR = 'freshdeskReceptor',
  JSM_RECEPTOR = 'jsmReceptor',
  CONFLUENCE_RECEPTOR = 'confluenceReceptor',
  ZENDESK_RECEPTOR = 'zendeskReceptor',
  TRUSTERO_INTELLIGENCE_V1 = 'trusteroIntelligenceV1',
  PINGONE_LOGIN = 'pingOneLogin',
  CITE_SOURCES = 'citeSources',
  HUBSPOT_RECEPTOR = 'hubspotReceptor',
  OKTA_RECEPTOR = 'oktaReceptor',
  TEST_TABLE = 'testTable',
  AZUREBOARDS_RECEPTOR = 'azureBoardsReceptor',
  USE_AI_QUESTION_USAGE_TYPE = 'useAiQuestionUsageType',
  FLAT_HELP_PAGE = 'flatHelpPage',
  RIPPLING_RECEPTOR = 'ripplingReceptor',
  TRUST_PORTAL = 'trustPortal',
  USE_INDEXING_STATUS_PAGE = 'useIndexingStatusPage',
  JIT_RECEPTOR = 'jitReceptor',
  SHAREPOINT = 'sharepoint',
  SHOW_CHAT_STATUS = 'showChatStatus',
  LEVEL_OF_DETAIL = 'levelOfDetail',
  RISK_CATEGORY_CONFIGURATION = 'riskCategoryConfiguration',
  JIRA_WORKFLOW = 'jiraWorkflow',
  AGENT_CHAT = 'agentChat',
  CHAT_SEARCH = 'chatSearch',
  SERVICENOW_WORKFLOW = 'servicenowWorkflow',
  GRC_DASHBOARDS = 'grcDashboards',
  NEW_TRUST_PORTAL = 'newTrustPortal',
  RISK_RESPONSE_DASHBOARD = 'riskResponseDashboard',
  DOCEBO_RECEPTOR = 'doceboReceptor',
  PLAYBOOK = 'playbook',
  SALESFORCE_RECEPTOR = 'salesforceReceptor',
  ONSPRING_RECEPTOR = 'onspringReceptor',
  CONFLUENCE_DMS = 'confluenceDMS',
  VIEW_LINKER_DOCUMENTS = 'viewLinkerDocuments',
  USE_SCHEDULING = 'useScheduling',
  PLAYBOOKS_UI = 'playbooksUi',
  CONTROL_EVIDENCE_MAPPINGS = 'controlEvidenceMappings',
  NEW_EVIDENCE_PAGE = 'newEvidencePage',
  APP_COPILOT = 'appCopilot',
  VENDOR_COPILOT = 'vendorCopilot',
  COPILOT_SHOW_PAGE = 'copilotShowPage',
  MULTIPLE_TEST_PROCEDURES = 'multipleTestProcedures',
  MULTI_POLICY_CONTROL_LINKAGES = 'multiPolicyControlLinkages',
  NEW_LOGIN_PAGE = 'newLoginPage',
  NEW_POLICY_PAGE = 'newPolicyPage',
  POLICY_FOLDER_SCAN = 'policyFolderScan',
  CUSTOM_CONTROL_STATUSES = 'customControlStatuses',
  CUSTOM_RISK_STATUSES = 'customRiskStatuses',
  CONTROL_STATUS_COLOR_PICKER = 'controlStatusColorPicker',
  SHOW_EDITED_TEST_DETAILS = 'showEditedTestDetails',
  INCIDENTIO = 'incidentioReceptor',
  CROWDSTRIKE_RECEPTOR = 'crowdstrikeReceptor',
  RAPID7_RECEPTOR = 'rapid7Receptor',
  NEW_REQUESTS = 'newRequests',
  LABELS = 'labels',
  OVERVIEW_DASHBOARD = 'overviewDashboard',
  OVERVIEW_DASHBOARD_DRAGGABLE = 'overviewDashboardDraggable',
  FRAMEWORK_REQUIREMENTS_TAB = 'frameworkRequirementsTab',
  CONTENT_UPGRADE = 'contentUpgrade',
  ALLOW_PLG_SIGNUP = 'allowPlgSignup',
  SOC2_TRUST_SERVICE_CRITERIA = 'soc2TrustServiceCriteria',
  REACT_PLAYBOOKS = 'ReActPlaybooks',
  REQUEST_TI_ANSWER = 'requestTiAnswer',
  // Routes the CSV controls upload through the new import-wizard staging path
  // (StageImportControls) instead of the legacy ImportControls path.
  CONTROL_IMPORT_WIZARD = 'controlImportWizard',
  PRODUCT_HELP_TI = 'productHelpTI',
  DSL_REPORTS = 'dslReports',
  ACTIVITIES_PAGE = 'activitiesPage',
  LABEL_RBAC = 'labelRbac',
  GRC_IMPORT_WIZARD = 'grcImportWizard',
  INTERNAL_ACCOUNT_MANAGEMENT = 'internalAccountManagement',
  TPRM_QUESTIONNAIRE = 'tprmQuestionnaire',
  SLIDING_EARLIEST_EVIDENCE = 'slidingEarliestEvidence',
  NEW_USER_ROLES_UI = 'newUserRolesUi',
  IF_GRANTED_PERMISSION_RULE = 'ifGrantedPermissionRule',
  // Mirrors ntrced's control_test_import flag; the import RPCs reject the call when it is off.
  CONTROL_TEST_IMPORT = 'controlTestImport',
}

export type FlagOverride = {
  enabled: boolean
  minAppVersion: string
}

/**
 * Feature flag overrides fetched from ntrced for this environment.
 *
 * Module-level rather than React state on purpose: IsGatekeeperEnabled is a
 * plain synchronous function called from 300+ sites, many outside a component,
 * so the resolved values have to be readable without a hook. FeatureFlagProvider
 * fills this once per page load, before rendering anything that reads it.
 */
let featureFlagOverrides: Record<string, FlagOverride> = {}

export const setFeatureFlagOverrides = (
  overrides: Record<string, FlagOverride>,
): void => {
  featureFlagOverrides = overrides
}

export const getFeatureFlagOverrides = (): Record<string, FlagOverride> =>
  featureFlagOverrides

/**
 * The stored override for a flag, or null when none applies.
 *
 * minAppVersion suppresses *enabling* only. An override that turns a flag on is
 * ignored by a client older than minAppVersion, so a newly shipped feature can't
 * light up in a stale cached bundle that lacks the code. An override that turns
 * a flag off always applies: that's the kill switch, and an old client running a
 * broken feature is exactly who it needs to reach.
 */
const dbOverrideValue = (flag: string): boolean | null => {
  const override = featureFlagOverrides[flag]
  if (!override) {
    return null
  }
  if (
    override.enabled &&
    override.minAppVersion &&
    isNewerVersion(
      override.minAppVersion,
      process.env.REACT_APP_SEMVER || '0.0.0',
    )
  ) {
    return null
  }
  return override.enabled
}

function isLocallyEnabled(flag: string): boolean | null {
  // for our internal use, we want to allow users to override the value
  // of feature flags by setting keys in localStorage
  // Keys will be named "<env>_feature_<flag>"
  // The default value of `null` allows us to tell the caller
  // that the value was not found in localStorage
  const value = loadFromStorage(`feature_${flag}`, null)
  return value
}

export const GatekeeperProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  // verify that all defined flags are in our enum
  const flagData =
    (EnvConfig.featureFlags as { [key in GatekeeperFlag]: boolean }) || {}
  const allowedFlags = Object.values(GatekeeperFlag)
  for (const flag of Object.keys(flagData) as GatekeeperFlag[]) {
    if (!allowedFlags.includes(flag)) {
      logger.error(`Unknown gatekeeper flag in config file: ${flag}`)
    }
  }
  // delegate to FlagsProvider
  const featureFlags = Object.entries(EnvConfig.featureFlags || {}).map(
    ([flag, isEnvEnabled]) => {
      return {
        name: flag,
        isActive: isLocallyEnabled(flag) ?? isEnvEnabled,
      }
    },
  )
  return <FlagsProvider value={featureFlags}>{children}</FlagsProvider>
}

/*
 * This function is used to determine if a feature flag is enabled.
 * @param flag - The gatekeeper flag(s) to check - can be a single flag or an array of flags
 * @returns true if all of the flags are enabled, false otherwise
 */

export const IsGatekeeperEnabled = (
  flag: GatekeeperFlag | GatekeeperFlag[],
): boolean => {
  // We only want to return true if all flags are enabled
  if (Array.isArray(flag)) {
    return !flag.some((f) => !IsGatekeeperEnabled(f))
  }

  const isEnvEnabled = EnvConfig?.featureFlags?.[flag] === true

  return isLocallyEnabled(flag) ?? dbOverrideValue(flag) ?? isEnvEnabled
}

type GatekeeperProps = {
  authorizedFlags: GatekeeperFlag[]
  children?: JSX.Element | JSX.Element[]
  renderOff?: () => JSX.Element | null
  renderOn?: () => JSX.Element | null
}

export const Gatekeeper = ({
  authorizedFlags,
  renderOff = () => null,
  renderOn = () => null,
  children,
}: GatekeeperProps): JSX.Element => {
  if (authorizedFlags.some((f) => !Object.values(GatekeeperFlag).includes(f))) {
    logger.error('Invalid Gatekeeper flag was passed in')
    return <></>
  }

  return (
    <Flags
      renderOff={renderOff}
      renderOn={renderOn}
      authorizedFlags={authorizedFlags}
    >
      {children}
    </Flags>
  )
}

type ReverseGatekeeperComponentProps = {
  shouldHide: <t>(arg: t) => boolean
  shouldHideArg: unknown
  authorizedFlags: GatekeeperFlag | GatekeeperFlag[]
  children?: JSX.Element | JSX.Element[]
}

/**
 * This component is used to hide a component if the gatekeeper flag is NOT enabled and the shouldHide function returns true
 * @param shouldHide - a function that takes in a generic argument and returns a boolean
 * @param shouldHideArg - the argument to pass to the shouldHide function
 * @param authorizedFlags - the gatekeeper flag(s) to check
 * @param children - the component to render if the gatekeeper flag is enabled or the shouldHide function returns false
 * @returns the null if the gatekeeper flag is NOT enabled and the shouldHide function returns true, otherwise it returns the children
 */
export const ReverseGatekeeperComponent = ({
  shouldHide,
  shouldHideArg,
  authorizedFlags,
  children,
}: ReverseGatekeeperComponentProps): JSX.Element => {
  const gatekeeper = IsGatekeeperEnabled(authorizedFlags)
  return !gatekeeper && shouldHide(shouldHideArg) ? <></> : <>{children}</>
}

/**
 * Determine feature access from the backend
 */
export const useHasFeatureAccess = (
  featureType: FEATURE_ACCESS,
  shouldFetch = true,
): GrpcResponse<FeatureAccessResponse> => {
  const { response } = useSwrImmutableGrpc(
    AccountPromiseClient.prototype.checkFeatureAccess,
    new FeatureAccessRequest().setFeatureType(featureType),
    shouldFetch && featureType > -1, // Ensure we don't call the API if the feature type is not valid (begins with 0 enum)
  )

  return NewGrpcResponse(response)
}

export const useHasGoogleDriveAuth = (): boolean => {
  const hasEnvAccess = IsGatekeeperEnabled(GatekeeperFlag.GOOGLE_DRIVE_AUTH)

  return hasEnvAccess
}

export const hasControlStatus = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.CONTINUOUS_MONITORING)

export const useHasGenerateSystemDescription = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.GENERATE_SYSTEM_DESCRIPTION)

export const useHasAnecdotesReceptor =
  (): GrpcResponse<FeatureAccessResponse> => {
    return useHasFeatureAccess(FEATURE_ACCESS.ANECDOTES_EVIDENCE_INDEX)
  }

export const useHasSlackNotification = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.SLACK_NOTIFICATION)
  return useMemo(() => data?.getAllowAccess() ?? false, [data])
}

export const useHasTeamsNotification = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.TEAMS_NOTIFICATION)
  return useMemo(() => data?.getAllowAccess() ?? false, [data])
}

export const useHasTrusteroIntelligence = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.TRUSTERO_INTELLIGENCE)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasRiskCategoryConfiguration = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.RISK_CATEGORY_CONFIGURATION)
}

export const useHasTrustPortalConfiguration = (): boolean => {
  const isProvider = useIsProvider()
  const isAdmin = useIsAdmin()
  const isTrustPortalPublisher = useIsTrustPortalPublisher()
  const isTrustPortalApprover = useIsTrustPortalApprover()
  const trustPortalAccountId = useTrustPortalAccountId()
  return (
    isProvider &&
    (isAdmin || isTrustPortalPublisher || isTrustPortalApprover) &&
    !!trustPortalAccountId
  )
}

export const useFlatHelpPage = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.FLAT_HELP_PAGE)

export const useIndexingStatusPage = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.USE_INDEXING_STATUS_PAGE)

export const useSharepointLinkPaste = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.USE_SHAREPOINT_LINK_PASTE)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useRipplingReceptor = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.RIPPLING_RECEPTOR)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useConfigLLMModel = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.LLM_MODEL_CONFIG)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasGRCContentCleanup = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.GRC_CONTENT_CLEANUP)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useHasContentPublishing = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.CONTENT_PUBLISHING)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

/**
 * True when this account is provisioned as a reporting parent, i.e. it may view aggregated
 * Operating Effectiveness results from its linked child accounts.
 *
 * Account-level entitlement only — on its own it does not authorize showing child data.
 * Surfacing that additionally requires a per-user permission check and at least one
 * consented, reporting child, so compose this rather than gating on it alone.
 */
export const useHasChildAccountReporting = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.CHILD_ACCOUNT_REPORTING)
  return useMemo(() => data?.getAllowAccess() || false, [data])
}

export const useIsGlobalContentAccount = (): boolean => {
  const { data: currentUser } = useUser()
  const { data: playbooks } = usePlaybooks({ includeGlobalPlaybooks: true })

  return useMemo(() => {
    if (!currentUser || !playbooks) return false
    const currentAccountId = currentUser.getAccountid()
    return playbooks
      .getPlaybooksList()
      .some((p) => p.getIsGlobal() && p.getAccountId() === currentAccountId)
  }, [currentUser, playbooks])
}

export const useAgentChat = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.AGENT_CHAT)
  return (
    data?.getAllowAccess() || IsGatekeeperEnabled(GatekeeperFlag.AGENT_CHAT)
  )
}

export const useHasGRCDashboards = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.GRC_DASHBOARDS)
  const { data: user } = useUser()
  const { data: featureAccess } = useHasFeatureAccess(
    FEATURE_ACCESS.GRC_DASHBOARDS,
  )
  const isTrustedUser = useMemo(() => {
    const email = user?.getEmail()
    return (
      (email?.toLowerCase().endsWith('@trustero.com') &&
        !isDeniedInternalEmail(email)) ||
      false
    )
  }, [user])
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || isTrustedUser || hasDashboards
}

export const useHasRiskDashboard = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.RISK_RESPONSE_DASHBOARD,
  )
  const { data: featureAccess } = useHasFeatureAccess(
    FEATURE_ACCESS.RISK_DASHBOARD,
  )
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || hasDashboards
}

export const useOverviewDashboardAccess = (): {
  hasAccess: boolean
  loading: boolean
} => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.OVERVIEW_DASHBOARD)
  const isInternalUser = useIsInternalUser()
  const { data: featureAccess, loading } = useHasFeatureAccess(
    FEATURE_ACCESS.OVERVIEW_DASHBOARD,
  )
  const hasDashboards = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  const hasAccess = isFlagEnabled || isInternalUser || hasDashboards
  return {
    hasAccess,
    loading: !hasAccess && loading,
  }
}

export const useHasOverviewDashboard = (): boolean => {
  const { hasAccess } = useOverviewDashboardAccess()
  return hasAccess
}

export const useHasOverviewDashboardDraggable = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.OVERVIEW_DASHBOARD_DRAGGABLE)

export const useHasPlaybook = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.PLAYBOOK)
}

export const useHasScheduling = (): boolean => {
  const isFlagEnabled = IsGatekeeperEnabled(GatekeeperFlag.USE_SCHEDULING)
  const { data: featureAccess } = useHasFeatureAccess(FEATURE_ACCESS.SCHEDULING)
  const hasAccess = useMemo(
    () => featureAccess?.getAllowAccess() || false,
    [featureAccess],
  )
  return isFlagEnabled || hasAccess
}

export const useHasPlaybooksUi = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.PLAYBOOKS_UI)
}

export const useHasNewEvidencePage = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.NEW_EVIDENCE_PAGE)
}

export const useHasNewPolicyPage = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.NEW_POLICY_PAGE)
}

export const useHasAppCopilot = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.APP_COPILOT)
  return (
    data?.getAllowAccess() || IsGatekeeperEnabled(GatekeeperFlag.APP_COPILOT)
  )
}

export const useHasCopilotShowPage = (): boolean => {
  const isInternalUser = useIsInternalUser()
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.COPILOT_SHOW_PAGE)
  return (
    data?.getAllowAccess() ||
    IsGatekeeperEnabled(GatekeeperFlag.COPILOT_SHOW_PAGE) ||
    isInternalUser
  )
}

export const useHasGenerateControls = (): boolean => {
  const { data } = useHasFeatureAccess(FEATURE_ACCESS.GENERATE_CONTROLS)
  return data?.getAllowAccess() || false
}

export const useHasCustomControlStatuses = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.CUSTOM_CONTROL_STATUSES)
}

export const useHasCustomRiskStatuses = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.CUSTOM_RISK_STATUSES)
}

export const useHasReActPlaybooks = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.REACT_PLAYBOOKS)

export const useHasTprmQuestionnaire = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.TPRM_QUESTIONNAIRE)

/**
 * Resolves every playbook feature_flag string (stored on the Playbook proto) to
 * whether this account may see playbooks carrying it.
 *
 * A hook rather than a Record of hooks: the hooks have to be called
 * unconditionally and in a stable order, which iterating a map cannot promise.
 * This replaced a `Record<string, () => boolean>` that nothing ever consumed --
 * the real filtering lived in a hand-maintained duplicate inside
 * CopilotLibraryView, so the exported map drifted out of use while every other
 * playbook surface filtered nothing at all.
 *
 * Add a line here when a migration starts stamping a new feature_flag value.
 */
export const useResolvedPlaybookFeatureFlags = (): Record<string, boolean> => {
  const hasReActPlaybooks = useHasReActPlaybooks()
  // Trustero-employee-only playbooks: visible only to @trustero.com users.
  const isInternalUser = useIsInternalUser()
  // Vendor questionnaire playbooks: gated on the same flag as the TPRM surfaces
  // they write into.
  const hasTprmQuestionnaire = useHasTprmQuestionnaire()

  return useMemo(
    () => ({
      ReAct_playbooks: hasReActPlaybooks,
      trustero_internal: isInternalUser,
      tprm_questionnaire: hasTprmQuestionnaire,
    }),
    [hasReActPlaybooks, isInternalUser, hasTprmQuestionnaire],
  )
}

export const useHasControlStatusColorPicker = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.CONTROL_STATUS_COLOR_PICKER)
}

export const useHasProductHelpTI = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.PRODUCT_HELP_TI)

export const useHasInternalAccountManagement = (): boolean => {
  return IsGatekeeperEnabled(GatekeeperFlag.INTERNAL_ACCOUNT_MANAGEMENT)
}
export const useHasActivitiesPage = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.ACTIVITIES_PAGE)

export const useHasGrcImportWizard = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.GRC_IMPORT_WIZARD)

export const useHasIfGrantedPermissionRule = (): boolean =>
  IsGatekeeperEnabled(GatekeeperFlag.IF_GRANTED_PERMISSION_RULE)
